import Config from '@/shared/config/config';
import { getRolloutGroupCookie, SERVED_ROLLOUT_GROUP } from '@/shared/lib/util/rolloutGroupCookie';
import { useCallback, useEffect, useState } from 'react';

const POLL_INTERVAL_MS = 5 * 60 * 1000;

const DISMISSED_VERSION_KEY = 'th_newReleaseDismissedVersion';

/**
 * The version of the build currently on the server, from the `version.json` that rspack/build.config.ts
 * writes next to index.html. undefined when it cannot be read (offline, mid-deploy, dev server).
 */
const fetchDeployedVersion = async (): Promise<string | undefined> => {
  try {
    const response = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
    if (!response.ok) return undefined;
    const { version } = await response.json();
    return typeof version === 'string' ? version : undefined;
  } catch {
    return undefined;
  }
};

// sessionStorage can throw (private mode, blocked storage). Without it, dismissal lasts until the
// page unloads, which is still fine.
const readDismissedVersion = (): string | undefined => {
  try {
    return sessionStorage.getItem(DISMISSED_VERSION_KEY) ?? undefined;
  } catch {
    return undefined;
  }
};

const saveDismissedVersion = (version: string) => {
  try {
    sessionStorage.setItem(DISMISSED_VERSION_KEY, version);
  } catch {
    // See readDismissedVersion.
  }
};

/**
 * Notices when a new build has been deployed while this tab is open, by comparing the running
 * package.json version with the one in `version.json`. Checks every 5 minutes and whenever the tab
 * becomes visible again.
 *
 * Only ever reports; it never reloads. People may be partway through a note or an encounter form, so
 * they choose when to refresh.
 *
 * package.json version rather than __BUILD_VERSION: that is the latest git tag, which has not moved
 * since v2025-05-16, so the banner would never show.
 */
export const useNewReleaseCheck = () => {
  const [deployedVersion, setDeployedVersion] = useState<string>();
  const [dismissedVersion, setDismissedVersion] = useState(readDismissedVersion);

  // Dev builds have no version.json, and a build without a version has nothing to compare.
  const enabled = process.env.NODE_ENV === 'production' && Config.APP_VERSION !== 'unknown';

  const check = useCallback(async () => {
    // When the rollout cookie no longer matches the build that was served, version.json comes from
    // the other target group. RolloutGroupPrompt already asks the user to reload in that case.
    if (getRolloutGroupCookie() !== SERVED_ROLLOUT_GROUP) return;

    const version = await fetchDeployedVersion();
    if (version) setDeployedVersion(version);
  }, []);

  useEffect(() => {
    if (!enabled) return;

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') check();
    };

    const interval = setInterval(check, POLL_INTERVAL_MS);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [enabled, check]);

  // Dismissal is for this release only, so a later deploy shows the banner again.
  const showBanner = !!deployedVersion && deployedVersion !== Config.APP_VERSION && deployedVersion !== dismissedVersion;

  const dismiss = () => {
    if (!deployedVersion) return;
    saveDismissedVersion(deployedVersion);
    setDismissedVersion(deployedVersion);
  };
  const reload = () => window.location.reload();

  return { showBanner, reload, dismiss };
};
